// PQ-tuning dialog store. Owns the discriminated-union state machine for
// the recalibration popout, plus per-system request IDs for stale-response
// guarding and a sync-tracking map that survives dialog unmount.
//
// Why a store (not component-local useReducer):
//   1. Dialog can close mid-sync. The 'syncing' state must persist so that
//      reopening returns directly to the syncing UI without re-dispatching.
//   2. State is read from two sources (HTTP GET + WS push). Centralizing
//      reconciliation here keeps the component thin.
//
// See plan: docs/plans/2026-05-15-feat-pq-tuning-dashboard-plan.md
// §"Dialog state lifecycle".

import { create } from "zustand";
import { api, ApiError } from "@/lib/api";
import type { InominalChange } from "@/lib/types";

export interface PqTuningData {
  system_id: string;
  node_id: string | null;
  current_inominal_amps: number | null;
  swell_threshold_a: number | null;
  swell_threshold_pct: number;
  inominal_status: "syncing" | "ok" | "failed" | "invalid" | null;
  inominal_synced_at: string | null;
  events_since_event_start: number | null;
  // Cache-derived; null during cold-start or when cache_stalled === true
  p95_current_30m: number | null;
  peak_current_15m: number | null;
  avg_current_15m: number | null;
  recommended_inominal_amps: number | null;
  events_last_hour: number | null;
  events_by_severity: Record<string, number> | null;
  dominant_severity: "severe" | "moderate" | "minor" | "none" | null;
  flagged: boolean | null;
  reason: "upward_noise" | "downward_miss" | "event_flood" | "ok" | null;
  last_refresh_at: string | null;
  cache_stalled: boolean;
  // U3 drift decision — null until the cache has evaluated this system.
  // retune_recommended is the drift-confirmed, stability-gated signal that
  // a retune is warranted; retune_reason carries the gate that decided it.
  retune_recommended: boolean | null;
  retune_reason: string | null;
  // U4 audit trail — every applied iNominal change, newest first.
  inominal_history: InominalChange[];
}

export type DialogState =
  | { kind: "closed" }
  | { kind: "loading"; systemId: string }
  | { kind: "cold-start"; systemId: string; reason: "warming" | "stalled" }
  | { kind: "ready"; systemId: string; data: PqTuningData; inputAmps: number }
  | { kind: "confirming"; systemId: string; data: PqTuningData; targetAmps: number }
  | { kind: "dispatching"; systemId: string; targetAmps: number }
  | { kind: "syncing"; systemId: string; targetAmps: number; dispatchedAt: number }
  | { kind: "success"; systemId: string; finalAmps: number }
  | { kind: "failed"; systemId: string; reason: string }
  | { kind: "invalid"; systemId: string; reason: string };

interface PqTuningStore {
  dialog: DialogState;
  // requestId per system, incremented on each fetch. Late responses with
  // mismatched IDs are dropped — fixes the race where WS push arrives before
  // a slow GET resolves and the slow GET would otherwise overwrite freshness.
  latestRequestId: Map<string, number>;

  // Per-meter opt-out from drift flagging. A meter in this set never lights
  // the PQ badge for drift/retune signals. Operator-owned, localStorage-
  // persisted — purely a client-side display preference (the cache still
  // computes the decision; the badge just suppresses it).
  flaggingOptOut: Set<string>;

  // Actions
  open: (systemId: string, windowId?: string) => Promise<void>;
  toggleFlaggingOptOut: (systemId: string) => void;
  close: () => void;
  setInput: (amps: number) => void;
  beginConfirm: (targetAmps: number) => void;
  cancelConfirm: () => void;
  submitRecalibrate: (windowId?: string) => Promise<void>;
  // Called by the dialog when it observes a terminal inominal_status via WS.
  // Idempotent — already-settled states are no-ops.
  onStatusFromWs: (
    systemId: string,
    status: "syncing" | "ok" | "failed" | "invalid" | null,
    inominalAmps?: number | null,
  ) => void;
}

const COLD_START_MAX_AGE_S = 300; // 5 min — dialog reads `cache_stalled` flag

// localStorage-backed per-meter drift-flagging opt-out. Best-effort: a
// missing/corrupt key or a storage-disabled browser degrades to "nothing
// opted out" rather than throwing.
const OPTOUT_KEY = "pq-tuning-flagging-optout";

function loadOptOut(): Set<string> {
  try {
    const raw = localStorage.getItem(OPTOUT_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    // Tolerate a structurally wrong stored value (object, number, tampered
    // data) — only string entries of an array count.
    return new Set(
      Array.isArray(parsed)
        ? parsed.filter((x): x is string => typeof x === "string")
        : [],
    );
  } catch {
    return new Set();
  }
}

function persistOptOut(s: Set<string>): void {
  try {
    localStorage.setItem(OPTOUT_KEY, JSON.stringify([...s]));
  } catch {
    /* storage disabled — opt-out is best-effort */
  }
}

async function fetchTuning(
  systemId: string,
  windowId?: string,
): Promise<PqTuningData> {
  const qs = windowId ? `?window_id=${encodeURIComponent(windowId)}` : "";
  return api.get<PqTuningData>(`/api/dyno/pq-tuning/${encodeURIComponent(systemId)}${qs}`);
}

export const usePqTuningStore = create<PqTuningStore>((set, get) => ({
  dialog: { kind: "closed" },
  latestRequestId: new Map(),
  flaggingOptOut: loadOptOut(),

  toggleFlaggingOptOut: (systemId) => {
    const next = new Set(get().flaggingOptOut);
    if (next.has(systemId)) next.delete(systemId);
    else next.add(systemId);
    persistOptOut(next);
    set({ flaggingOptOut: next });
  },

  open: async (systemId, windowId) => {
    // Bump the request ID so any in-flight fetch from a previous open
    // becomes a stale response and gets dropped.
    const latest = get().latestRequestId;
    const myId = (latest.get(systemId) ?? 0) + 1;
    latest.set(systemId, myId);

    set({ dialog: { kind: "loading", systemId } });
    try {
      const data = await fetchTuning(systemId, windowId);
      // Drop the response if a newer open or close happened in the meantime.
      if (get().latestRequestId.get(systemId) !== myId) return;
      const currentDialog = get().dialog;
      if (currentDialog.kind === "closed" || currentDialog.systemId !== systemId) return;

      // If the server says we're mid-sync, mount in syncing state. The
      // dispatched-at fallback uses now() if we don't have an exact value.
      if (data.inominal_status === "syncing") {
        set({
          dialog: {
            kind: "syncing",
            systemId,
            targetAmps: 0, // unknown; this is a re-mount, not a dispatch
            dispatchedAt: data.inominal_synced_at
              ? new Date(data.inominal_synced_at).getTime()
              : Date.now(),
          },
        });
        return;
      }

      // Cold-start: cache not warm yet, or stalled
      if (data.cache_stalled) {
        set({ dialog: { kind: "cold-start", systemId, reason: "stalled" } });
        return;
      }
      if (data.recommended_inominal_amps == null && data.events_last_hour == null) {
        set({ dialog: { kind: "cold-start", systemId, reason: "warming" } });
        return;
      }

      const defaultInput =
        data.recommended_inominal_amps ?? data.current_inominal_amps ?? 15;
      set({
        dialog: { kind: "ready", systemId, data, inputAmps: defaultInput },
      });
    } catch (e) {
      // Drop the error if the request is stale.
      if (get().latestRequestId.get(systemId) !== myId) return;
      console.warn("pq-tuning fetch failed", e);
      set({
        dialog: {
          kind: "failed",
          systemId,
          reason: e instanceof Error ? e.message : String(e),
        },
      });
    }
  },

  close: () => {
    // Closing doesn't cancel an in-flight sync — cloud-api dispatched it in
    // a background thread already and will write terminal status to
    // system_registry regardless of dialog visibility. Always transition to
    // 'closed' so the visual actually goes away. On reopen, `open` re-fetches
    // and remounts in 'syncing' if the server still says so (server is the
    // source of truth, not the in-memory dialog state). `onStatusFromWs`
    // already no-ops when kind === 'closed', so a terminal status arriving
    // after the user closes doesn't pop the dialog back open uninvited.
    set({ dialog: { kind: "closed" } });
  },

  setInput: (amps) => {
    const d = get().dialog;
    if (d.kind === "ready") {
      set({ dialog: { ...d, inputAmps: amps } });
    }
  },

  beginConfirm: (targetAmps) => {
    const d = get().dialog;
    if (d.kind === "ready") {
      set({ dialog: { kind: "confirming", systemId: d.systemId, data: d.data, targetAmps } });
    }
  },

  cancelConfirm: () => {
    const d = get().dialog;
    if (d.kind === "confirming") {
      set({
        dialog: { kind: "ready", systemId: d.systemId, data: d.data, inputAmps: d.targetAmps },
      });
    }
  },

  submitRecalibrate: async (windowId) => {
    const d = get().dialog;
    if (d.kind !== "confirming") return;
    const { systemId, targetAmps } = d;
    set({ dialog: { kind: "dispatching", systemId, targetAmps } });

    try {
      await api.post(`/api/dyno/pq-tuning/${encodeURIComponent(systemId)}/recalibrate`, {
        amps: targetAmps,
      });
      // 202 returned. Transition to syncing — WS will push terminal status.
      set({
        dialog: {
          kind: "syncing",
          systemId,
          targetAmps,
          dispatchedAt: Date.now(),
        },
      });
    } catch (e) {
      if (e instanceof ApiError) {
        const body = (e.body as { error?: string }) ?? {};
        if (e.status === 409) {
          // Another operator already dispatched; mount in syncing to
          // track theirs. We don't know targetAmps for sure — leave it
          // as our intended value and let WS reconcile.
          set({
            dialog: {
              kind: "syncing",
              systemId,
              targetAmps,
              dispatchedAt: Date.now(),
            },
          });
          return;
        }
        if (e.status === 400) {
          set({
            dialog: {
              kind: "invalid",
              systemId,
              reason: body.error ?? "amps_rejected",
            },
          });
          return;
        }
      }
      console.warn("pq-tuning recalibrate failed", e);
      set({
        dialog: {
          kind: "failed",
          systemId,
          reason: e instanceof Error ? e.message : String(e),
        },
      });
    }
    void windowId; // currently unused; window scope is server-derived
  },

  onStatusFromWs: (systemId, status, inominalAmps) => {
    const d = get().dialog;
    // Only react if this WS update is for the system in the dialog.
    if (d.kind === "closed" || d.systemId !== systemId) return;
    // Idempotent — if we've already settled, ignore.
    if (
      d.kind === "success" || d.kind === "failed" || d.kind === "invalid"
    ) {
      return;
    }
    if (status === "ok") {
      set({
        dialog: {
          kind: "success",
          systemId,
          finalAmps: inominalAmps ?? (d.kind === "syncing" ? d.targetAmps : 0),
        },
      });
    } else if (status === "failed") {
      set({
        dialog: { kind: "failed", systemId, reason: "recalibration_failed" },
      });
    } else if (status === "invalid") {
      set({
        dialog: { kind: "invalid", systemId, reason: "meter_rejected_value" },
      });
    }
    // status === 'syncing' or null: no transition, just keep current state
  },
}));

export { COLD_START_MAX_AGE_S };
